import type { UpdateAutomationCategoryRequest } from '@reach/shared-types/automation-category';
import { useToast } from '../../../../hooks/useToast';
import { useDeleteAutomationCategory, useUpdateAutomationCategory } from '../queries';

/** Save and delete for one partner category, with failure toasts. Used by CategoryEditDialog. */
export function useCategoryEditor(args: {
  categoryId: string;
  onDone: () => void;
  onDeleted?: () => void;
}) {
  const { categoryId, onDone, onDeleted } = args;
  const { addToast } = useToast();
  const updateCategory = useUpdateAutomationCategory();
  const deleteCategory = useDeleteAutomationCategory();

  const showFailure = (title: string) => (error: Error) =>
    addToast({ title, description: error.message, variant: 'destructive' });

  const save = (changes: UpdateAutomationCategoryRequest) =>
    updateCategory.mutate(
      { categoryId, changes },
      { onSuccess: onDone, onError: showFailure('Could not save the category') },
    );

  const remove = () =>
    deleteCategory.mutate(categoryId, {
      onSuccess: () => {
        onDeleted?.();
        onDone();
      },
      onError: showFailure('Could not delete the category'),
    });

  return { save, remove, isPending: updateCategory.isPending || deleteCategory.isPending };
}
